// Firestore schema — Oracle data model.
// Shape definitions + lightweight validators for the 4 Phase 0 entities.

export const userSchema = {
    uid: "string",                       // Firebase Auth UID
    username: "string | null",
    email: "string",
    created_at: "Timestamp",
    age_attested: "boolean",             // Phase 0 age-gate
    legacy_migrated: "boolean",          // True if user existed before the pivot
    display_name: "string | null",       // Phase 1 Profile work expands
    // Onboarding wizard (4-step: age → gender → handle → terms).
    age: "number | null",                // 16..80 self-reported (scan calibration)
    gender: "string | null",             // male | female | non_binary | prefer_not_to_say
    onboarding_complete: "boolean",      // gate flag — false until the wizard finishes
    onboarding_completed_at: "string | null", // ISO timestamp
    terms_accepted_at: "string | null",  // ISO timestamp
    // First-run interactive tutorial (post-onboarding overlay). New docs start
    // false; legacy docs omit it (undefined). Gated on !== true, so both see the
    // tour once (the site was revamped) until it's finished/skipped → true.
    tutorial_complete: "boolean",        // gate flag — !== true means show the tour
    tutorial_completed_at: "string | null", // ISO timestamp
    // Lifecycle email state (server-managed; written by reminder cron + unsubscribe).
    email_optout: "boolean",             // true = unsubscribed from reminder emails (absent = opted in)
    last_reminder_at: "Timestamp | null",// last inactivity reminder sent — re-nag gate
    // Peer-notification state (server-managed; Admin SDK only, so NOT in
    // firestore.rules hasOnly()). The per-hour burst cap lives here; the
    // per-scan cadence that decides WHETHER to send lives on the scan/glowup
    // doc as `feedback_notify`. See functions/emailFunctions/burstCap.js and
    // notifyCadence.js.
    feedback_email_slots: "{ bucket: number, count: number } | null", // scan notifications this hour
    glowup_email_slots: "{ bucket: number, count: number } | null",   // glowup notifications this hour
    last_feedback_email_at: "Timestamp | null", // last scan notification delivered
    last_glowup_email_at: "Timestamp | null",   // last glowup notification delivered
    // Daily scan quota (server-managed; written by runOraclePipeline via the
    // Admin SDK — never client-writable, so NOT in firestore.rules hasOnly()).
    // See functions/oracleFunctions/scanQuota.js for the allowances and the
    // legacy-doc fallback.
    scan_day: "string | null",           // UTC YYYY-MM-DD the counter below applies to
    scan_day_count: "number",            // scans spent on scan_day (1/day free, 5/day pro)
    // DEPRECATED, read-only: the pre-counter quota pair. `last_free_scan_date`
    // is still READ as a fallback for docs written before the counter shipped
    // (see scansUsedToday); `scan_unlock_credits` is dead entirely — the
    // offerwall that granted it was removed. Neither is written any more.
    // Both can be purged from existing docs in a cleanup pass.
    last_free_scan_date: "string | null",
    scan_unlock_credits: "number",
};

export const scanSchema = {
    scan_id: "string",                   // uuid v4, also doc ID
    user_id: "string | null",            // null = anonymous Phase 0 scan
    timestamp: "Timestamp",
    raw_image_path: "string",            // storage path, private bucket
    processed_image_url: "string",       // signed URL
    verdict: {
        overall_score: "number",         // 1-100
        vectors: "array<{name, score}>",
        top_3_improvement_vectors: "array<string>",
    },
    recommendations: "array<{vector, products}>", // denormalized at scan time
};

export const productSchema = {
    product_id: "string",                // doc ID
    vector: "string",                    // one of 8 vector names
    brand: "string",
    product_name: "string",
    image_url: "string",
    affiliate_url: "string",
    price_range: "string",               // e.g. "$15-30"
    description: "string",
    active: "boolean",
    curated_priority: "number",          // 1-10
};

// Server event log. Written ONLY by Cloud Functions (Admin SDK) — the client
// has no access in either direction (firestore.rules). One collection for all
// server-authoritative funnel events; see functions/contracts/analytics.js for
// the event list and why each kill-gate metric is measured server-side.
export const analyticsEventSchema = {
    event: "string",                     // EVENTS.* from contracts/analytics
    ts: "Timestamp",                     // serverTimestamp
    uid: "string | null",
    scan_id: "string | null",
    props: "object",                     // small, flat, no PII
};

export const affiliateClickSchema = {
    timestamp: "Timestamp",
    scan_id: "string",                   // which scan triggered the click
    user_id: "string | null",
    product_id: "string",
    vector: "string",
    source: "verdict_card",              // for future tracking expansion
};

// Default document templates — what a freshly-created doc looks like.

export const DEFAULT_USER_DATA = {
    uid: "",
    username: null,
    email: "",
    created_at: null,
    age_attested: false,
    legacy_migrated: false,
    display_name: null,
    age: null,
    gender: null,
    onboarding_complete: false,
    onboarding_completed_at: null,
    terms_accepted_at: null,
    tutorial_complete: false,
    tutorial_completed_at: null,
};

// Validators. Return null when valid, otherwise an object map of field → message.

const isString = (v) => typeof v === "string";
const isBool = (v) => typeof v === "boolean";
const isNumber = (v) => typeof v === "number" && !Number.isNaN(v);
const isStringOrNull = (v) => v === null || isString(v);

export const validateUser = (data) => {
    const errors = {};
    if (!isString(data.uid) || !data.uid) errors.uid = "uid is required";
    if (!isString(data.email)) errors.email = "email is required";
    if (!isStringOrNull(data.username)) errors.username = "username must be string or null";
    if (!isStringOrNull(data.display_name)) errors.display_name = "display_name must be string or null";
    if (!isBool(data.age_attested)) errors.age_attested = "age_attested must be boolean";
    if (data.legacy_migrated !== undefined && !isBool(data.legacy_migrated)) {
        errors.legacy_migrated = "legacy_migrated must be boolean";
    }
    // Onboarding fields — optional; validated only when present (legacy docs omit them).
    if (data.age !== undefined && data.age !== null && !isNumber(data.age)) {
        errors.age = "age must be a number or null";
    }
    if (data.gender !== undefined && !isStringOrNull(data.gender)) {
        errors.gender = "gender must be string or null";
    }
    if (data.onboarding_complete !== undefined && !isBool(data.onboarding_complete)) {
        errors.onboarding_complete = "onboarding_complete must be boolean";
    }
    if (data.onboarding_completed_at !== undefined && !isStringOrNull(data.onboarding_completed_at)) {
        errors.onboarding_completed_at = "onboarding_completed_at must be string or null";
    }
    if (data.tutorial_complete !== undefined && !isBool(data.tutorial_complete)) {
        errors.tutorial_complete = "tutorial_complete must be boolean";
    }
    if (data.tutorial_completed_at !== undefined && !isStringOrNull(data.tutorial_completed_at)) {
        errors.tutorial_completed_at = "tutorial_completed_at must be string or null";
    }
    if (data.terms_accepted_at !== undefined && !isStringOrNull(data.terms_accepted_at)) {
        errors.terms_accepted_at = "terms_accepted_at must be string or null";
    }
    if (data.email_optout !== undefined && !isBool(data.email_optout)) {
        errors.email_optout = "email_optout must be boolean";
    }
    // Daily scan quota — server-managed, optional (legacy docs omit both).
    if (data.last_free_scan_date !== undefined && !isStringOrNull(data.last_free_scan_date)) {
        errors.last_free_scan_date = "last_free_scan_date must be string or null";
    }
    if (data.scan_unlock_credits !== undefined && !isNumber(data.scan_unlock_credits)) {
        errors.scan_unlock_credits = "scan_unlock_credits must be a number";
    }
    return Object.keys(errors).length > 0 ? errors : null;
};

export const validateScan = (data) => {
    const errors = {};
    if (!isString(data.scan_id) || !data.scan_id) errors.scan_id = "scan_id is required";
    if (!isStringOrNull(data.user_id)) errors.user_id = "user_id must be string or null";
    if (!isString(data.raw_image_path)) errors.raw_image_path = "raw_image_path is required";
    if (!isString(data.processed_image_url)) errors.processed_image_url = "processed_image_url is required";
    if (!data.verdict || typeof data.verdict !== "object") {
        errors.verdict = "verdict object is required";
    } else {
        if (!isNumber(data.verdict.overall_score)) errors["verdict.overall_score"] = "overall_score must be number";
        if (!Array.isArray(data.verdict.vectors)) errors["verdict.vectors"] = "vectors must be an array";
        if (!Array.isArray(data.verdict.top_3_improvement_vectors)) {
            errors["verdict.top_3_improvement_vectors"] = "top_3_improvement_vectors must be an array";
        }
    }
    if (data.recommendations !== undefined && !Array.isArray(data.recommendations)) {
        errors.recommendations = "recommendations must be an array";
    }
    return Object.keys(errors).length > 0 ? errors : null;
};

export const validateProduct = (data) => {
    const errors = {};
    if (!isString(data.product_id) || !data.product_id) errors.product_id = "product_id is required";
    if (!isString(data.vector) || !data.vector) errors.vector = "vector is required";
    if (!isString(data.brand)) errors.brand = "brand is required";
    if (!isString(data.product_name)) errors.product_name = "product_name is required";
    if (!isString(data.image_url)) errors.image_url = "image_url is required";
    if (!isString(data.affiliate_url)) errors.affiliate_url = "affiliate_url is required";
    if (!isString(data.price_range)) errors.price_range = "price_range is required";
    if (!isString(data.description)) errors.description = "description is required";
    if (!isBool(data.active)) errors.active = "active must be boolean";
    if (!isNumber(data.curated_priority)) errors.curated_priority = "curated_priority must be number";
    return Object.keys(errors).length > 0 ? errors : null;
};

export const validateAffiliateClick = (data) => {
    const errors = {};
    if (!isString(data.scan_id) || !data.scan_id) errors.scan_id = "scan_id is required";
    if (!isStringOrNull(data.user_id)) errors.user_id = "user_id must be string or null";
    if (!isString(data.product_id) || !data.product_id) errors.product_id = "product_id is required";
    if (!isString(data.vector) || !data.vector) errors.vector = "vector is required";
    if (!isString(data.source) || !data.source) errors.source = "source is required";
    return Object.keys(errors).length > 0 ? errors : null;
};
